import { useState, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { formatAmounts, plural } from '@aytm/app/stats/lib/helpers'

import { useStuckToTop } from '@aytm/app/stats/page/components/lib/hooks'
import { toHeaderStats } from '@aytm/app/stats/page/data-quality-report/lib/header-stats'

import { ReportControls } from '@aytm/app/stats/page/sidebar/reports/ReportControls'

import style from '@aytm/app/stats/page/components/styles/StickyHeader.module.scss'

const ValueLoader = () => (
  <span className={style['report-meta-value-loader']}>
    <span></span>
    <span></span>
    <span></span>
  </span>
)

// A quality layer that never landed carries null rather than 0, and dashes here like any other
// figure the report does not have.
const qualityFigure = value => (null == value ? '—' : formatAmounts(value))

const StickyHeader = ({
  url,
  mode,
  state,
  surveyId,
  surveyCompleted,
  totals,
  controls = {},
  isSurveyGuest,
  isSurveyViewer,
  isReportLoading,
  isReportVirtual,
  isReportXpert,
  dqrController,
  onFetch,
  onModeChange,
  onShowReportPopup
}) => {
  const dqrReport = dqrController?.getReport?.() || null
  const qualityStats = dqrReport ? toHeaderStats(dqrReport) : null
  // Terminal failure (error, no committed report) — the same signal the report body reads for
  // its LOAD_FAILED state. Without this the counters below would loop on the loader forever.
  const reportFailed = Boolean(dqrController?.hasError?.()) && !dqrReport

  const [isStuck, stickyRef] = useStuckToTop()

  // The /dqr report lands on the DQR controller — subscribe to the same seam the report body uses,
  // so the header swaps its loaders for real figures on the same notify.
  const [, forceRender] = useState(0)

  const onClick = useCallback(
    (event, mode) => {
      event.preventDefault()

      onModeChange(mode)
    },
    [onModeChange]
  )

  useEffect(() => dqrController?.subscribe?.(() => forceRender(n => n + 1)), [dqrController])

  return (
    <div ref={stickyRef} className={classNames(style.component, style[state], { [style.stuck]: isStuck })}>
      <div className={style['component-content']}>
        <div className={style['report-meta']}>
          <div className={style['report-meta-row']}>
            <span className={style['report-meta-title']}>N:</span>
            {isReportLoading ? (
              <ValueLoader />
            ) : (
              <span className={style['report-meta-value']}>
                {formatAmounts(totals.responses)}/{totals.respondents}
              </span>
            )}
            <span className={style['report-meta-title']}>ID:</span>
            <span className={style['report-meta-value']}>{surveyId}</span>
          </div>
          <div className={style['report-meta-row']}>
            <span className={style['report-meta-title']}>Completed:</span>
            <span className={style['report-meta-value']}>{surveyCompleted}</span>
          </div>
        </div>
        {'data-quality' == state ? (
          // DQR figures are report-specific; a virtual/Xpert report has no DQR, so only this block is null-guarded.
          isReportVirtual || isReportXpert ? null : (
            <div className={style['report-quality']}>
              <div className={style['report-quality-row']}>
                <span className={style['report-quality-title']}>Human Review removals:</span>
                {qualityStats ? (
                  <span className={style['report-quality-value']}>{qualityFigure(qualityStats.rejectedCount)}</span>
                ) : reportFailed ? (
                  <span className={style['report-quality-value']}>—</span>
                ) : (
                  <ValueLoader />
                )}
              </div>
              <div className={style['report-quality-row']}>
                <span className={style['report-quality-title']}>Blocked survey attempts:</span>
                {qualityStats ? (
                  <span className={style['report-quality-value']}>{qualityFigure(qualityStats.blockedCount)}</span>
                ) : reportFailed ? (
                  <span className={style['report-quality-value']}>—</span>
                ) : (
                  <ValueLoader />
                )}
              </div>
            </div>
          )
        ) : (
          <>
            {controls.state && controls.model ? (
              <div className={style['report-controls']}>
                <ReportControls
                  url={url}
                  state={controls.state}
                  model={controls.model}
                  listState={controls.listState}
                  listModel={controls.listModel}
                  limits={controls.limits}
                  actions={controls.actions}
                  virtualReportsIds={controls.virtualReportsIds}
                  isCompact={true}
                  isSurveyGuest={isSurveyGuest}
                  isSurveyViewer={isSurveyViewer}
                  disabled={controls.disabled}
                />
                <span className={style['report-controls-config']} onClick={onShowReportPopup}>
                  <i className="icon-aytmfd-config" />
                </span>
              </div>
            ) : null}
            {isReportVirtual || isReportXpert ? null : (
              <div className={style['report-switches']}>
                {'charts' == state ? null : (
                  <a
                    className={style['report-switches-button']}
                    href={`${window.location.href.replace(window.location.hash.split('/')[0], '#charts')}`}
                    rel="noreferrer"
                    onClick={event => onClick(event, 'charts')}
                  >
                    <i className="icon-aytmfd-quant-report-v2" />
                    <span>
                      Quant
                      <br />
                      report
                    </span>
                  </a>
                )}
                {['charts', 'specials'].includes(state) ? (
                  <>
                    {0 < totals.totalComments || 'comments' == mode ? (
                      <a
                        className={classNames(
                          `${style['report-switches-button']} ${style['report-switches-button-comments']}`,
                          { [style['report-switches-button-active']]: 'comments' == mode }
                        )}
                        href={`${window.location.href.replace(window.location.hash.split('/')[0], '#comments')}`}
                        rel="noreferrer"
                        onClick={'comments' == mode ? null : event => onClick(event, 'comments')}
                      >
                        <i className="icon-aytmfd-comments-v2" />
                        <span>
                          {formatAmounts(totals.totalComments)}
                          <br />
                          {plural(totals.totalComments, 'verbatim', 'verbatims')}
                        </span>
                      </a>
                    ) : null}
                    {0 < totals.totalImageResponses || 'image-responses' == mode ? (
                      <a
                        className={classNames(
                          `${style['report-switches-button']} ${style['report-switches-button-image-responses']}`,
                          { [style['report-switches-button-active']]: 'image-responses' == mode }
                        )}
                        href={`${window.location.href.replace(window.location.hash.split('/')[0], '#image-responses')}`}
                        rel="noreferrer"
                        onClick={'image-responses' == mode ? null : event => onClick(event, 'image-responses')}
                      >
                        <i className="icon-aytmfd-image-responses-v2" />
                        <span>
                          {formatAmounts(totals.totalImageResponses)} image
                          <br />
                          {plural(totals.totalImageResponses, 'response', 'responses')}
                        </span>
                      </a>
                    ) : null}
                    {0 < totals.totalVideoResponses || 'video-responses' == mode ? (
                      <a
                        className={classNames(
                          `${style['report-switches-button']} ${style['report-switches-button-video-responses']}`,
                          { [style['report-switches-button-active']]: 'video-responses' == mode }
                        )}
                        href={`${window.location.href.replace(window.location.hash.split('/')[0], '#video-responses')}`}
                        rel="noreferrer"
                        onClick={'video-responses' == mode ? null : event => onClick(event, 'video-responses')}
                      >
                        <i className="icon-aytmfd-video-responses-v2" />
                        <span>
                          {formatAmounts(totals.totalVideoResponses)} video
                          <br />
                          {plural(totals.totalVideoResponses, 'response', 'responses')}
                        </span>
                      </a>
                    ) : null}
                  </>
                ) : null}
              </div>
            )}
          </>
        )}
        {'data-quality' == state ? null : (
          <div className={style['report-refresh']}>
            <span
              className={classNames(style['report-refresh-button'], {
                [style['report-refresh-button-spinning']]: isReportLoading
              })}
              onClick={isReportLoading ? null : onFetch}
            >
              <i className={classNames('icon-aytmfd-refresh-v2', { 'aytmfd-spin': isReportLoading })} />
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

StickyHeader.propTypes = {
  url: PropTypes.string,
  mode: PropTypes.string,
  state: PropTypes.string,
  surveyId: PropTypes.number,
  surveyTitle: PropTypes.string,
  surveyStatus: PropTypes.string,
  surveyCompleted: PropTypes.string,
  report: PropTypes.object,
  totals: PropTypes.object,
  controls: PropTypes.object,
  isSurveyGuest: PropTypes.bool,
  isSurveyViewer: PropTypes.bool,
  isReportLoading: PropTypes.bool,
  isReportVirtual: PropTypes.bool,
  isReportXpert: PropTypes.bool,
  dqrController: PropTypes.object,
  onFetch: PropTypes.func,
  onModeChange: PropTypes.func,
  onShowReportPopup: PropTypes.func
}

StickyHeader.displayName = 'StickyHeader'

export default StickyHeader
